import { useState, useMemo } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { LogIn, ShieldCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/context/AuthContext'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'

function formatApiError(err: any): string {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((item: any) => item.msg || JSON.stringify(item)).join('. ')
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail)
  }
  return 'Login failed. Please check your credentials and try again.'
}

export function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [needsTotp, setNeedsTotp] = useState(false)
  const [touched, setTouched] = useState({ email: false, password: false })

  const emailError = useMemo(() => {
    if (!email) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address'
    return ''
  }, [email])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ email: true, password: true })
    setError('')

    if (emailError || !password) {
      return
    }
    if (needsTotp && totpCode.length < 6) {
      setError('Please enter the 6-digit authentication code')
      return
    }

    setLoading(true)
    try {
      await login(email, password, needsTotp ? totpCode : undefined)
      navigate('/')
    } catch (err: any) {
      const message = formatApiError(err)
      // CHECKPOINT: PRD-10 FR-10.11.2 — Prompt for TOTP when API signals 2FA is required.
      if (
        err?.response?.status === 403 &&
        message.toLowerCase().includes('two-factor authentication code required')
      ) {
        setNeedsTotp(true)
        setError('Enter the code from your authenticator app to continue.')
      } else {
        setError(message)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1.5">
        <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {searchParams.get('account_deleted') === '1' && (
          <AuthAlert
            variant="success"
            message="Your PurpleToad Mail account and all associated service data were permanently deleted."
          />
        )}
        {error && <AuthAlert message={error} />}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            aria-invalid={touched.email && !!emailError}
            aria-describedby={touched.email && emailError ? 'email-error' : undefined}
            required
            autoFocus
            disabled={needsTotp}
          />
          {touched.email && emailError && (
            <p id="email-error" className="text-xs text-destructive">{emailError}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete={needsTotp ? 'off' : 'current-password'}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            aria-invalid={touched.password && !password}
            aria-describedby={touched.password && !password ? 'password-error' : undefined}
            required
            disabled={needsTotp}
          />
          {touched.password && !password && (
            <p id="password-error" className="text-xs text-destructive">Password is required</p>
          )}
        </div>

        {needsTotp && (
          <div className="space-y-2">
            <Label htmlFor="totp" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Two-factor code
            </Label>
            <Input
              id="totp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              required
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Open your authenticator app and enter the 6-digit code.
            </p>
          </div>
        )}

        <AuthSubmitButton loading={loading}>
          <LogIn className="mr-2 h-4 w-4" />
          {needsTotp ? 'Verify and sign in' : 'Sign in'}
        </AuthSubmitButton>

        {needsTotp && (
          <button
            type="button"
            onClick={() => {
              setNeedsTotp(false)
              setTotpCode('')
              setError('')
            }}
            className="text-xs text-muted-foreground hover:text-primary"
          >
            Use a different sign-in method
          </button>
        )}
      </form>

      <div className="text-center text-sm">
        <span className="text-muted-foreground">Don't have an account? </span>
        <Link to="/register" className="text-primary hover:underline font-medium">Sign up</Link>
      </div>
    </div>
  )
}
